import React, { ComponentType, FC } from 'react'
import styles from './PolaroidList.module.scss'
import Polaroid, { PolaroidSpecificPageProps } from './Polaroid'
import { SAArtwork } from 'types/Artwork'
import { PolaroidListSkeleton } from './components/PolaroidSkeleton'
import classNames from 'classnames'
import useQA from 'hooks/useQA'
import useSearchProvider, {
  ConstructorAttributeObject,
} from 'components/SA/SearchProvider/hooks/useSearchProvider'

type CustomPolaroidListProps = PolaroidSpecificPageProps & {
  artworks: ReadonlyArray<SAArtwork>
  isLoading?: boolean
  skeletonQuantity?: number
  userID?: number
  customStyle?: string
  customPromoCode?: string
  searchProviderAttributes?: ReadonlyArray<ConstructorAttributeObject>
  imageSuffix?: string
  shouldSendAlgoliaEvents?: boolean
  CustomPolaroidHeader?: ComponentType<{ artwork: SAArtwork }>
  CustomPolaroidInfo: ComponentType<{ artwork: SAArtwork }>
  isPrintsPage?: boolean
  shouldIncludeSearchProviderAttributes?: boolean
  customArtworkClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
  polaroidInfoSpanCount?: number
}

const CustomPolaroidList: FC<CustomPolaroidListProps> = ({
  artworks,
  algoliaIndice,
  algoliaUserToken,
  dataApi,
  dataList,
  dataListId,
  dataListName,
  isLoading = false,
  skeletonQuantity = 6,
  userID,
  customStyle = '',
  customPromoCode,
  searchProviderAttributes,
  imageSuffix,
  shouldSendAlgoliaEvents,
  CustomPolaroidHeader,
  CustomPolaroidInfo,
  isPrintsPage,
  shouldIncludeSearchProviderAttributes = true,
  customArtworkClick,
  polaroidInfoSpanCount,
}) => {
  const { getSearchProviderAttributes } = useSearchProvider()
  const { testId } = useQA()

  if (isLoading) {
    return <PolaroidListSkeleton skeletonQuantity={skeletonQuantity} customStyle={customStyle} />
  }

  if (!artworks?.length) {
    return null
  }

  return (
    <div
      className={classNames(styles.polaroidListContainer, {
        [customStyle]: customStyle !== '',
      })}
      {...testId('polaroid-list-container')}
      {...(!!searchProviderAttributes ? getSearchProviderAttributes(searchProviderAttributes) : {})}
    >
      {artworks.map((artwork, index) => {
        const priority = index === 0
        const lazyLoad = index > 3
        const isImageSrcFromProd = index < 6
        return (
          <Polaroid
            key={artwork.artworkId}
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            priority={priority}
            lazyLoad={lazyLoad}
            isImageSrcFromProd={isImageSrcFromProd}
            imageSuffix={imageSuffix}
            userID={userID}
            customPromoCode={!!customPromoCode ? customPromoCode : ''}
            shouldSendAlgoliaEvents={shouldSendAlgoliaEvents}
            CustomPolaroidHeader={CustomPolaroidHeader}
            CustomPolaroidInfo={CustomPolaroidInfo}
            isPrintForDataLayerEvent={isPrintsPage}
            hasPrints={isPrintsPage}
            shouldIncludeSearchProviderAttributes={shouldIncludeSearchProviderAttributes}
            customArtworkClick={customArtworkClick}
            polaroidInfoSpanCount={polaroidInfoSpanCount}
          />
        )
      })}
    </div>
  )
}

export default CustomPolaroidList
